import { useServiceHistoryProofQuery, useServiceHistoryProofUploadMutation } from '../../../api';
import styles from './styles.module.scss';
import { ServiceHistoryProofOfHistoryModel } from 'api-new/generated';
import FilePreviewWrapper from 'components/file-preview';
import { InlineSvg } from 'components/svg';
import { useAuth } from 'features/auth/contexts/auth';
import { ChangeEvent, FC, useRef, useState } from 'react';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/scss/image-gallery.scss';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ButtonWithIcon } from 'shared';
import { formatDate } from 'utils/formats';

type Props = {
  serviceHistoryId?: number | null;
};

export const ProofOfServiceHistorySection: FC<Props> = ({ serviceHistoryId }) => {
  const [openFileUrl, setOpenFileUrl] = useState<string | null>(null);
  const [openIndexFile, setOpenIndexFile] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const imageGalleryRef = useRef<any>(null);
  const inputFile = useRef<HTMLInputElement>(null);
  const imageGalleryContainerRef = useRef<HTMLDivElement>(null);
  const { currentUser } = useAuth();
  const { mutate, isLoading } = useServiceHistoryProofUploadMutation();

  const { data, isFetching, refetch } = useServiceHistoryProofQuery(
    { serviceHistoryId: serviceHistoryId ?? 0 },
    { enabled: !!serviceHistoryId }
  );

  const handleUploadClick = () => {
    inputFile?.current?.click();
  };

  const isDoc = (fileName?: string | null): boolean => {
    return fileName?.trim().endsWith('.pdf') ?? false;
  };

  const openPreview = (x: ServiceHistoryProofOfHistoryModel) => {
    if (
      data?.proofOfHistory &&
      data.proofOfHistory.length > 0 &&
      imageGalleryContainerRef.current?.style.display === 'none'
    ) {
      const fileIds =
        data?.proofOfHistory?.filter((item) => !isDoc(item.fileUrl) && item.fileUrl).map((item) => item.id) ?? [];
      setOpenIndexFile(fileIds.indexOf(x.id));
      if (imageGalleryContainerRef.current) {
        imageGalleryContainerRef.current.style.display = 'block';
      }
      imageGalleryRef.current?.toggleFullScreen();
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files.length || !serviceHistoryId || !currentUser?.userId) return;

    mutate(
      {
        serviceHistoryId: serviceHistoryId,
        userId: currentUser.userId,
        file: e.target.files[0]
      },
      {
        onSuccess: () => {
          refetch();
        }
      }
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>Proof of Service History</div>
      {!data && (
        <div className={styles.emptyContainer}>
          <div className={styles.emptyTitle}>Proof of Service History not found</div>
          <div>
            <ButtonWithIcon onClick={handleUploadClick} isLoading={isLoading}>
              Upload more
            </ButtonWithIcon>
          </div>
        </div>
      )}

      {data && (
        <div>
          <div className={styles.createdInfoColumn}>
            <div>
              Last update - {data.serviceHistoryProofEditDate ? formatDate(data.serviceHistoryProofEditDate) : '-'} by{' '}
              {data.serviceHistoryProofEditUser ?? '-'}
            </div>

            <ButtonWithIcon onClick={handleUploadClick} isLoading={isLoading}>
              Upload more
            </ButtonWithIcon>
          </div>

          <div className={styles.scrollContainer}>
            <div className={styles.imageContainer}>
              {data.proofOfHistory?.map((x) => (
                <button
                  key={x.id}
                  type="button"
                  className="image"
                  onClick={() => (isDoc(x.fileUrl) ? setOpenFileUrl(x.fileUrl ?? null) : openPreview(x))}
                >
                  {!isDoc(x.fileUrl) ? (
                    <img src={x.fileUrl ?? ''} alt={x.fileName ?? ''} />
                  ) : (
                    <div className={styles.docContainer}>
                      <div>
                        <InlineSvg path="/media/icons/custom/pdf.svg" />
                      </div>
                      <div>{x.fileName}</div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {(isFetching || isLoading) && (
        <div className={styles.skeletonContainer}>
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} height={170} width={160} />
          ))}
        </div>
      )}
      {openFileUrl && <FilePreviewWrapper urls={[openFileUrl]} onClose={() => setOpenFileUrl(null)} />}

      <div style={{ position: 'absolute', inset: '0', display: 'none', zIndex: 1060 }} ref={imageGalleryContainerRef}>
        <button
          type="button"
          className={styles.closePreview}
          onClick={() => {
            imageGalleryRef.current?.toggleFullScreen();
          }}
        >
          x
        </button>
        <ImageGallery
          ref={imageGalleryRef}
          lazyLoad={true}
          useBrowserFullscreen={false}
          showFullscreenButton={false}
          startIndex={openIndexFile}
          items={
            data?.proofOfHistory
              ?.filter((x) => !isDoc(x.fileUrl) && x.fileUrl)
              .map((x) => ({
                original: x.fileUrl ?? '',
                thumbnail: x.fileUrl ?? '',
                renderItem: undefined
              })) ?? []
          }
          onScreenChange={(fullScreen) => {
            if (!fullScreen) {
              if (imageGalleryContainerRef.current) {
                imageGalleryContainerRef.current.style.display = 'none';
              }
              document.body.style.overflow = 'auto';
              document.documentElement.style.overflow = 'auto';
              return;
            }
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
          }}
        />
      </div>
      <input
        type="file"
        multiple={false}
        ref={inputFile}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept="image/png,image/jpeg,image/jpg,application/pdf"
      />
    </div>
  );
};
